import { useEffect, useRef, useState } from 'react'
import useQuery from 'hooks/useQuery'
import getAccessToken from 'hooks/getAccessToken'

const { REACT_APP_API_URL } = process.env

// Mismo criterio que Facturar.js (poll cada 2500ms mientras 'queued'/'polling') —
// acá el pipeline es más pesado (map-reduce sobre TODA la historia clínica,
// no una sola emisión), así que el intervalo es un poco más laxo.
const POLL_INTERVAL_MS = 3000

// Bug real ya encontrado y corregido en Facturar.js: sin un tope de tiempo
// de espera EN VIVO, un poll que sigue fallando (o que sigue devolviendo
// 'running' de verdad) deja el spinner colgado para siempre. 5 minutos, no
// los 30s de Facturar.js — esto puede tardar de verdad con historias largas.
export const HCE_SUMMARY_GIVE_UP_MS = 5 * 60 * 1000

// Bug real (revisión 2026-08-11): pasado el timeout de espera EN VIVO,
// cortar el polling del todo (como hace Facturar.js#pollQuery en su paso
// 'queued-long') dejaba la ÚNICA vía de refresco en manos del botón
// "Generar de nuevo" — que además el backend rechaza con 429 mientras la
// corrida real sigue viva, y sólo ESE rechazo dispara el refetch que
// actualiza la pantalla (ver el catch de generate() más abajo). Si el
// profesional no clickea ese botón (razonable: dice "generar de nuevo", no
// "revisar estado"), el panel queda mostrando "sigue corriendo, actualizá
// esta pantalla en un rato" para siempre, sin que nada lo actualice solo.
// Mucho más espaciado que el polling activo (no hay urgencia visual que
// justificar) pero sin apagarlo — así el panel SÍ se actualiza solo cuando
// la corrida real termine.
const GIVE_UP_POLL_INTERVAL_MS = 20000

export const HCE_SUMMARY_IN_PROGRESS_STATUSES = ['queued', 'running']

/**
 * Modo de prueba sin IA, para QA: con `?hceFake=1` en la URL, el POST de
 * generar manda el header que el backend usa para saltear la llamada al LLM
 * (ver api/src/hce-summary/fake.js).
 *
 * Va por query param y no por un botón: es una herramienta de prueba, no una
 * función del producto, y un botón "generar resumen falso" en la HCE es
 * exactamente lo que no queremos que un profesional encuentre.
 *
 * Es seguro dejar esto en el bundle de producción: el header no hace NADA sin
 * la variable de entorno del backend, que sólo existe en QA. Sin el gate del
 * server esto sería una vía para pedir texto falso desde el navegador.
 */
const wantsFakeSummary = () => {
  try {
    return new URLSearchParams(window.location.search).get('hceFake') === '1'
  } catch {
    return false
  }
}

const postJson = async (endpoint, accessToken, body, extraHeaders) => {
  const res = await fetch(`${REACT_APP_API_URL}/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(extraHeaders || {}),
    },
    body: JSON.stringify(body || {}),
  })
  const data = await res.json().catch(() => null)
  if (!res.ok) {
    const error = new Error(data?.error || `http_${res.status}`)
    error.status = res.status
    error.response = data
    throw error
  }
  return data
}

/**
 * Hook de datos del resumen de HCE con IA — GET/enabled y GET/latest vía
 * useQuery (reusa la autenticación Auth0 del resto de la app, mismo patrón
 * que ArcaStatusIndicator/Facturar.js). Las escrituras (generar, calificar)
 * van con `fetch` directo en vez de `hooks/useMutation`: la lógica de sync
 * de cache de ese hook matchea por substring del endpoint
 * (`endpoint.includes("consumers")`) y esta URL SIEMPRE contiene
 * "consumers" — inyectaría la respuesta `{id, status}` del POST en el
 * cache de la lista de pacientes del team (mismo motivo documentado en
 * hooks/useUpload.js, que ya usa fetch directo por la misma razón).
 */
export function useHceSummaryApi({ consumerID, enabled: enabledFromCaller } = {}) {
  const accessToken = getAccessToken()
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState(null)
  const [gaveUp, setGaveUp] = useState(false)
  // Sincronizado desde latestQuery.data?.status vía efecto (abajo) — NO se
  // puede leer el resultado de un useQuery dentro de las opciones de ese
  // mismo useQuery en el mismo render (mismo motivo por el que
  // Facturar.js#pollQuery usa `step`, un state separado, para su propio
  // refetchInterval en vez de mirar su propia `data`).
  const [pending, setPending] = useState(false)
  const pollStartedAt = useRef(null)
  // Bug real (revisión 2026-08-11, mismo patrón que useClipboard/
  // HceSummaryRating en esta misma feature): sin este guard, cerrar el
  // panel o cambiar de paciente mientras generate() está en vuelo dispara
  // setGenerating/setGenerateError sobre una instancia ya desmontada —
  // mismo mountedRef/safeSetState ya usado en Print.js.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])
  const safeSetState = (setter, value) => { if (mountedRef.current) setter(value) }

  // PERFORMANCE: el flag puede venir del caller (`enabled`), y cuando viene NO
  // se pide de nuevo.
  //
  // Feedback de la reunión del 19/08: "la búsqueda de resúmenes anteriores tarda".
  // La causa medible no era la query de Postgres (tiene índice por
  // consumer_id+resource_id+created_at) sino un waterfall de dos requests
  // SERIALIZADOS en el frontend: este hook pedía `/hce-summary/enabled` y recién
  // cuando resolvía habilitaba el GET de runs/latest. Y ese primer request es
  // redundante: el panel sólo se monta desde el botón de la toolbar, que existe
  // porque `useBetaFeatures` (un único `/features` para las tres betas) ya
  // resolvió que el flag está prendido. O sea: se pagaba un round-trip completo
  // para volver a averiguar algo que el padre ya sabía, y la búsqueda ni
  // arrancaba hasta que terminara.
  //
  // Se mantiene el auto-fetch cuando el caller NO pasa el flag: el hook sigue
  // siendo usable solo, y su contrato público (`enabled`) no cambia.
  const flagFromCaller = typeof enabledFromCaller === 'boolean'
  const enabledQuery = useQuery(window._l('/hce-summary/enabled').substr(1), {
    staleTime: 30000,
    enabled: !flagFromCaller,
  })
  const enabled = flagFromCaller ? enabledFromCaller : enabledQuery.data?.enabled === true

  const latestQuery = useQuery(
    window._l(`/hce-summary/consumers/${consumerID}/runs/latest`).substr(1),
    {
      enabled: enabled && !!consumerID,
      refetchInterval: pending ? (gaveUp ? GIVE_UP_POLL_INTERVAL_MS : POLL_INTERVAL_MS) : false,
    }
  )

  const run = latestQuery.data || null
  const status = run?.status

  useEffect(() => {
    setPending(HCE_SUMMARY_IN_PROGRESS_STATUSES.includes(status))
  }, [status])

  // Ventana de espera en vivo — mismo bug-class ya corregido en Facturar.js:
  // depender solo de `.data` deja de re-evaluar este efecto si el poll
  // empieza a fallar (network blip, o el team pierde el flag a mitad de
  // camino) — `dataUpdatedAt`/`errorUpdatedAt` cambian en CADA intento,
  // éxito o error.
  useEffect(() => {
    if (!pending || gaveUp) { if (!pending) pollStartedAt.current = null; return }
    if (!pollStartedAt.current) pollStartedAt.current = Date.now()
    if (Date.now() - pollStartedAt.current > HCE_SUMMARY_GIVE_UP_MS) setGaveUp(true)
  }, [pending, gaveUp, latestQuery.dataUpdatedAt, latestQuery.errorUpdatedAt])

  // Una corrida NUEVA (id distinto — "generar de nuevo" tras completar o
  // fallar) arranca su propia ventana de espera desde cero.
  useEffect(() => { setGaveUp(false); pollStartedAt.current = null }, [run?.id])

  // Bug real (pasada de revisión 2026-08-11): el refetch de confirmación
  // estaba DENTRO del mismo try que el POST — si el POST tenía éxito pero
  // el refetch fallaba (blip de red, 401 momentáneo por refresh de
  // token), el catch de abajo trataba esto como si la GENERACIÓN hubiera
  // fallado (mostraba "no pudimos generar el resumen" sobre algo que sí
  // se generó). Un refetch que falla después de una escritura exitosa es
  // un problema de LECTURA transitorio — nunca debe pisar el resultado
  // real de la escritura. `.catch(() => {})` en vez de un try/catch
  // aparte: no hay nada que hacer con el error salvo no propagarlo, el
  // próximo poll natural (o la próxima apertura del panel) va a traer el
  // estado real.
  /**
   * `range` = `{from, to}` en YYYY-MM-DD (feedback 19/08). Ausente = toda la
   * historia, que es el default del backend y el comportamiento de siempre.
   *
   * Se manda sólo el borde que exista: un `from: null` explícito y la ausencia
   * de la clave significan lo mismo del lado del servidor, pero mandar strings
   * vacíos no — el validador los trata como "sin límite" igual, y de paso el
   * body queda legible en el Network tab.
   */
  const generate = async (range = {}) => {
    setGenerating(true)
    setGenerateError(null)
    try {
      const body = {}
      if (range?.from) body.from = range.from
      if (range?.to) body.to = range.to

      const data = await postJson(
        window._l(`/hce-summary/consumers/${consumerID}/runs`).substr(1),
        accessToken.value,
        body,
        wantsFakeSummary() ? { 'x-hce-summary-fake': '1' } : null
      )
      await latestQuery.refetch().catch(() => {})
      return data
    } catch (error) {
      safeSetState(setGenerateError, error)
      // Un 429 (cooldown/tope diario) significa que YA hay una corrida real
      // en curso o reciente — refrescar acá evita que la pantalla se quede
      // mostrando "generar resumen" mientras en realidad hay una corrida
      // (posiblemente ya completa) que el usuario todavía no vio.
      if (error.status === 429) await latestQuery.refetch().catch(() => {})
      throw error
    } finally {
      safeSetState(setGenerating, false)
    }
  }

  const rate = async (runId, rating, comment) => {
    const data = await postJson(
      window._l(`/hce-summary/runs/${runId}/rating`).substr(1),
      accessToken.value,
      { rating, comment }
    )
    // Mismo criterio que generate(): el POST de calificación ya tuvo
    // éxito — un refetch que falla acá no debe hacer que rate() rechace
    // (HceSummaryRating trataría eso como "la calificación falló" y
    // revertiría el thumbs seleccionado, pese a que el servidor SÍ la
    // guardó).
    await latestQuery.refetch().catch(() => {})
    return data
  }

  return {
    enabled, enabledQuery,
    latestQuery, run,
    inProgress: pending && !gaveUp, gaveUp,
    generate, generating, generateError,
    rate,
  }
}

export default useHceSummaryApi
